import { useState, memo, useCallback, useEffect } from 'react';
import { Group, Line } from 'react-konva';
import Konva from 'konva';
import { useDispatch, useSelector } from 'react-redux';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { RootState } from 'store';
import {
	setUpateEffectedFacets,
	updateAddGutterLine,
	updateLineType,
} from 'store/slices/RoofToolLinesSlice';
import { KonvaEventObject } from 'konva/lib/Node';
import { FACET_HIGHLIGHT_COLOR } from '../../../constants';
import Label from './Label';
import { CreateSpecialEdge, UpdateSpecialEdge } from 'store/slices/RoofToolLinesSlice/types';
import { doesGutterLinesOverlap, doesSpecialEdgeOverlap, isSpecialFacetLine, updateDiscardFlag } from './utils';

interface Props extends Konva.LineConfig {
	segmentId: string;
	coordinates: number[][];
	label: string;
}

const RoofToolFacets = ({ segmentId, coordinates, label }: Props) => {
	const dispatch = useDispatch();
	const scale = useKonvaImageScale();

	const {
		currentFacetIndex,
		currentSelectedLine,
		activeGutterLine,
		createSpecialEdges,
		updateSpecialEdges,
		selectedFacets,
		effectedFacets
	} = useSelector((state: RootState) => state.roofToolLines.data);
	const shouldListen = isSpecialFacetLine(currentSelectedLine) || activeGutterLine;
	const isCurrentFacet = currentFacetIndex === segmentId;
	const showFacetLabels = useSelector((state: RootState) => state.roofEditData.data.showFacetLabels);
	const [hoveredFacetId, setHoveredFacetId] = useState<string | null>(null);

	const handleFacetClick = useCallback(
		(e: KonvaEventObject<PointerEvent>) => {
			if(!effectedFacets.some(eachFacet => eachFacet.facetId === segmentId)) return;
			const stage = e.target.getStage();
			if(stage) {
				stage.container().style.cursor = '';
			}
			const shape = e.target as Konva.Shape;
			if ('fill' in shape) {
				shape.fill('rgba(0, 150, 136, 0.4)');
				shape.getLayer()?.batchDraw();
			}
			const doesOverLapGutterLine = doesGutterLinesOverlap(selectedFacets);

			const [createEdgeData, updateEdgeData] = [createSpecialEdges, updateSpecialEdges].map(edges =>
				updateDiscardFlag(doesSpecialEdgeOverlap(edges), segmentId)
			);

			const allEdges = [...(createEdgeData?.edges ?? []), ...(updateEdgeData?.edges ?? [])];

			const hasActiveEdges = allEdges.some(edge => edge.discard === false);

			if (doesOverLapGutterLine) {
				const isLineExist = selectedFacets[segmentId]?.newAddedGutterLines
					?.find(eachLine =>
						effectedFacets.some(eachEffectedLine => eachEffectedLine.lineId === eachLine.id)
					);
				if (isLineExist) {
					const updatingLine = { ...isLineExist, discard: !(isLineExist.facetId === segmentId) };
					console.log(updatingLine.id);
					setTimeout(() => {
						dispatch(setUpateEffectedFacets({ facets: [] }));
						dispatch(updateAddGutterLine({ updateLines: [updatingLine] }));
					}, 250); // intentional delay for highlight color
				} else {
					return;
					// dispatch(undoRoofToolLines());
				}
			}

			if (hasActiveEdges) {
				setTimeout(() => {
					dispatch(setUpateEffectedFacets({ facets: [] }));
					dispatch(
						updateLineType({
							updateLines: [],
							createSpecialEdgesData: (createEdgeData ?? {}) as CreateSpecialEdge,
							updateSpecialEdgesData: (updateEdgeData ?? {}) as UpdateSpecialEdge,
						})
					);
				}, 250); // intentional delay for highlight color
			} else if (allEdges.length > 0 && (!createEdgeData || !updateEdgeData)) {
				console.log('nope', hasActiveEdges);
				return;
			}

			console.log(createEdgeData, updateEdgeData);

			// dispatch(setCurrentFacet({ currentFacetIndex: segmentId }));
		},
		[dispatch, segmentId, createSpecialEdges, updateSpecialEdges, selectedFacets, effectedFacets]
	);

	return (
		<Group>
			<Line
				points={coordinates.flat()}
				scale={scale}
				fill={effectedFacets.some(eachFacet => eachFacet.facetId === segmentId) ? (hoveredFacetId ? 'rgba(0, 150, 136, 0.4)' : FACET_HIGHLIGHT_COLOR) : 'rgba(0, 128, 255, 0.5)'}
				closed={true}
				stroke={'yellow'}
				strokeWidth={1}
				listening={shouldListen}
				onPointerEnter={(e) => {
					const stage = e.target.getStage();
					if(!stage) return;
					if(effectedFacets.length === 0) return;
					const cursor = effectedFacets.some(eachFacet => eachFacet.facetId === segmentId) ? 'pointer' : 'not-allowed';
					stage.container().style.cursor = cursor;
					setHoveredFacetId(segmentId);
				}}
				onPointerOut={() => setHoveredFacetId(null)}
				onPointerClick={handleFacetClick}
				cursor={
					effectedFacets.some(eachFacet => eachFacet.facetId === segmentId) ? 'not-allowed' : 'pointer'
				}
			/>
			<Label
				simplifiedHullCoords={coordinates}
				label={label}
				visible={showFacetLabels}
				roofSegId={segmentId}
			/>
		</Group>
	);
};

export default memo(RoofToolFacets);